import { memo } from "react";
import { Box, Chip, Icon, Link, Typography } from "@saviynt/design-system";
import type { Policy, PolicyTag } from "@repo/api/schemas/policies.schema";
import { getPoliciesColor } from "../PoliciesPageListColumnConfig";
import { capitalizeFirstLetter, formatDateShort } from "@repo/mfe-core";
import { useNavigate } from "@tanstack/react-router";

export const formatTtrToDays = (ttr: number): string => {
  const days = Math.floor(ttr / (1000 * 60 * 60 * 24));
  return `${days} days`;
};

export const TimestampCell = memo(({ timestamp }: { timestamp: number }) => (
  <TextCell text={timestamp === 0 ? "-" : formatDateShort(timestamp)} />
));

export const TextCell = memo(
  ({
    text = null,
    lineLimit = 1,
    className = "",
  }: {
    text?: string | number | null;
    lineLimit?: number;
    className?: string;
  }) => (
    <Typography
      kind="body2"
      desktop="body2-d"
      mobile="body2-d"
      color="--color-foreground-secondary-bold"
      lineLimit={lineLimit}
      tooltipConfigs={{ placement: "left" }}
      className={className}
    >
      {text ?? String("-")}
    </Typography>
  ),
);

export const TextBoldCell = memo(
  ({ text = null }: { text: string | number | null }) => (
    <Typography
      kind="body2-bold"
      desktop="body2-bold-d"
      mobile="body2-bold-d"
      color="--color-foreground-secondary-bold"
      lineLimit={1}
      tooltipConfigs={{ placement: "left" }}
    >
      {text ?? String("-")}
    </Typography>
  ),
);

export const LinkCell = memo(
  ({
    text,
    onClick,
    isDisabled = false,
  }: {
    text: string | number;
    onClick?: (event: React.MouseEvent) => void;
    isDisabled?: boolean;
  }) => (
    <Link
      text={text}
      color="brand"
      onClick={onClick}
      target="_self"
      isDisabled={isDisabled}
    />
  ),
);

export const PolicyTypeIcon = memo(
  ({ isCustomPolicy }: { isCustomPolicy: boolean }) => (
    <Icon
      kind={isCustomPolicy ? "User" : "JobControlPanel"}
      color="--color-foreground-teal-subtle"
      size="large"
    />
  ),
);

export const PolicyNameCell = memo(({ row }: { row: Policy }) => (
  <div className="flex policy-name-cell">
    <Box className="flex items-center justify-center pr-2">
      <PolicyTypeIcon isCustomPolicy={row.is_custom} />
    </Box>
    <Box>
      <Box className="flex items-center gap-2">
        <TextBoldCell text={row.name} />
        <Chip
          kind="subtleFilled"
          label={`v${row.version}`}
          color="neutral"
          size="small"
        />
      </Box>
      {row.is_custom && !!row.parent_policy_name ? (
        <Chip
          kind="subtleFilled"
          label={`From: ${row.parent_policy_name}`}
          color="info"
          size="small"
          leftIcon={<Icon kind="Branch" size="xSmall" />}
        />
      ) : null}
      {row.description && <TextCell text={row.description} lineLimit={3} />}
    </Box>
  </div>
));

export const SeverityCell = memo(
  ({ severity }: { severity: Policy["severity"] }) => (
    <Chip
      kind="subtleFilled"
      label={capitalizeFirstLetter(severity)}
      color={getPoliciesColor(severity)}
      className={severity}
      dataTestId={`severity-chip-${severity}`}
    />
  ),
);

export const FindingsCell = memo(({ row }: { row: Policy }) => {
  const navigate = useNavigate();
  const isDisabled = row.attribution_count === 0;

  return (
    <LinkCell
      text={row.attribution_count}
      isDisabled={isDisabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate({
          to: "/findings",
          search: { name: row.name, status: "Open", category: row.category },
        });
      }}
    />
  );
});

export const TagsCell = memo(
  ({ row, systemTags }: { row: Policy; systemTags: PolicyTag[] }) => {
    const { metadata } = row;

    const rawTags = metadata?.tags;
    const tags = Array.isArray(rawTags)
      ? rawTags
      : rawTags
        ? rawTags.split(",")
        : [];

    return (
      <Box className="flex flex-wrap gap-2">
        {tags.length === 0 && <TextCell text="-" />}
        {tags.map((tagId) => {
          const tagDetails = systemTags.find((t) => t.id === tagId);
          const tagLink = tagDetails?.link;
          return (
            <Chip
              key={tagId}
              kind="subtleFilled"
              color="brandPrimary"
              label={
                <>
                  {tagLink ? (
                    <Typography kind="body3" lineLimit={1}>
                      {tagDetails.id}
                    </Typography>
                  ) : (
                    <Typography kind="body3" lineLimit={1}>
                      {tagDetails?.id ?? tagId}
                    </Typography>
                  )}
                </>
              }
              onClick={
                tagLink
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation();
                      window.open(tagDetails.link, "_blank", "noopener,origin");
                    }
                  : undefined
              }
              rightIcon={
                tagLink ? <Icon kind="JumpLink" size="xSmall" /> : undefined
              }
              size="small"
              isInteractionDisabled={!tagLink}
            />
          );
        })}
      </Box>
    );
  },
);
